import type { SupabaseAppointmentResolutionLogRow } from '../types/supabase';
import { supabase } from '../config/supabase';
import { IntegrationService } from './IntegrationService';
import { InvoiceService } from './InvoiceService';
import {
  AppointmentPaymentOrigin,
  resolveAppointmentPaymentOriginForBilling,
  resolveAppointmentPaymentOriginPatch,
} from '../utils/appointmentPaymentOrigin';


export interface PendingAppointment {
  id: string; // appointmentId
  title: string; // appointmentTitle
  start: Date; // appointmentStartDate
  end: Date; // appointmentEndDate
  calendar_name: string; // userAssignedCalendarName (do user, via join)
  location?: string; // appointmentLocation
  patientId?: string; // patientId
  patientName?: string; // patientFullName (do patient, via join)
  hoursSincePassed: number;
  urgencyLevel: 'high' | 'medium' | 'low';
  // Novos campos do Supabase para o agendamento
  appointmentStatus: 'scheduled' | 'completed' | 'cancelled' | 'no_show';
  appointmentPaymentOrigin?: AppointmentPaymentOrigin | null;
  professionalId?: string; // userId do profissional
  clientId?: string;
}

export interface NoShowOpenInvoiceCandidate {
  invoiceId: string;
  invoiceMonthReference: string;
  invoiceTotalAmount: number;
  invoiceStatus: string;
  invoiceCreatedAt?: string | null;
  invoiceDueDate?: string | null;
  invoiceNotes?: string | null;
}

export interface NoShowChargePreview {
  absenceNumber: number;
  willBeCharged: boolean;
  chargeAmount: number;
  monthReference: string;
  openInvoices: NoShowOpenInvoiceCandidate[];
  requiresInvoiceSelection: boolean;
}

export interface AppointmentStatusResolution {
  appointmentId: string;
  action: 'completed' | 'no_show' | 'cancelled';
  evolutionId?: string;
  notes?: string;
  resolvedBy: string;
  resolvedAt: Date;
}

export interface AppointmentResolutionAuditEntry {
  id: string;
  appointmentId: string;
  clientId: string;
  patientId?: string | null;
  actorUserId?: string | null;
  actorDisplayName?: string | null;
  actorEmail?: string | null;
  previousStatus?: string | null;
  newStatus?: string | null;
  action: SupabaseAppointmentResolutionLogRow['resolutionAction'];
  notes?: string | null;
  absenceInfo?: any;
  details?: any;
  createdAt: string;
}

interface PendingAppointmentsFilters {
  clientId?: string;
  professionalName?: string;
  userId?: string;
  userRole?: string;
}

type ReopenCandidateKind = 'package_usage' | 'invoice_item';

export interface ReopenNoEvaluatedCandidate {
  id: string;
  kind: ReopenCandidateKind;
  title: string;
  subtitle?: string;
  amount?: number;
  meta: Record<string, any>;
}

export interface ReopenNoEvaluatedPreviewResult {
  canReopen: boolean;
  requiresSelection: boolean;
  blockingReasons: string[];
  appointment: {
    appointmentId: string;
    clientId: string;
    patientId: string | null;
    appointmentStatus: string | null;
    appointmentEvolutionId: string | null;
    appointmentIsBilled: boolean;
    appointmentStartDate: string;
    appointmentUpdatedAt?: string | null;
    resolvedInvoiceId: string | null;
  } | null;
  candidates: ReopenNoEvaluatedCandidate[];
}

export interface ReopenNoEvaluatedActor {
  userId: string;
  userRole?: string;
  displayName?: string;
  email?: string;
  clientId: string;
}

export interface ConfirmReopenNoEvaluatedParams {
  clientId: string;
  actor: ReopenNoEvaluatedActor;
  selectedCandidateId?: string | null;
  expectedAppointmentUpdatedAt?: string | null;
}

export interface ConfirmReopenNoEvaluatedResult {
  appointmentId: string;
  previousStatus: string | null;
  newStatus: 'scheduled';
  reversalApplied: {
    kind: ReopenCandidateKind;
    id: string;
  } | null;
}

export type AbsenceDecisionOrigin = 'none' | 'pacote' | 'avulsa';
export type AbsenceFinancialImpact = 'none' | 'credit_consumption' | 'monetary_charge' | 'both';

export interface AbsenceDecisionOption {
  optionId: string;
  origin: AbsenceDecisionOrigin;
  contractId?: string | null;
  contractPricingRuleId?: string | null;
  financialPackageId?: string | null;
  entitlementId?: string | null;
  title: string;
  subtitle?: string | null;
  absenceNumber: number;
  chargeFromAbsence: number;
  periodFrom: string;
  periodUntil: string;
  countingPeriodType: 'calendar_month' | 'calendar_year' | 'rolling_days';
  isChargeable: boolean;
  financialImpact: AbsenceFinancialImpact;
  amount: number;
  available: boolean;
  blocker?: string | null;
  policySource: string;
  requiresFollowUp?: boolean;
  policySnapshot?: Record<string, unknown>;
}

export interface AbsenceDecisionPreview {
  appointmentId: string;
  patientId: string;
  patientName: string;
  appointmentDate: string;
  appointmentStatus: string;
  defaultAbsenceNumber: number;
  options: AbsenceDecisionOption[];
  existingDecision?: Record<string, unknown> | null;
}

export interface RegisterAbsenceDecisionInput {
  clientId: string;
  appointmentId: string;
  absenceType: 'justified' | 'unjustified';
  optionId?: string | null;
  notes?: string;
  idempotencyKey: string;
}

export interface RegisterAbsenceDecisionResult {
  absenceEventId: string;
  idempotent: boolean;
  status: 'pending' | 'applied' | 'reversed';
  financialImpact: AbsenceFinancialImpact;
  decisionOrigin?: AbsenceDecisionOrigin;
  absenceNumber?: number;
  amount?: number;
}

export type FinancialOriginKind = 'pacote' | 'plano' | 'avulsa';

export interface FinancialOriginOption {
  id: string;
  kind: FinancialOriginKind;
  packageId?: string | null;
  contractId?: string | null;
  contractPricingRuleId?: string | null;
  serviceType?: string | null;
  title: string;
  subtitle?: string | null;
  totalAmount?: number | null;
  payerAmount?: number | null;
  patientAmount?: number | null;
  requiresAuthorization?: boolean;
  recommended?: boolean;
  recommendationReason?: string | null;
}

export interface FinancialOriginPreview {
  appointmentId: string;
  patientId: string | null;
  appointmentStartDate: string;
  serviceType: string | null;
  recommendedId: string | null;
  options: FinancialOriginOption[];
  noChargeAvailable: boolean;
}

export interface CompleteAppointmentFinancialSelection {
  origin: 'pacote' | 'avulsa';
  serviceType?: string | null;
  contractPricingRuleId?: string | null;
  financialPackageId?: string | null;
}

export interface CompleteAppointmentResult {
  created?: boolean;
  idempotent?: boolean;
  origin?: 'pacote' | 'avulsa';
  billableItemId?: string | null;
  financialInvoiceId?: string | null;
  invoiceGenerated?: boolean;
  grouped?: boolean;
}

export class AppointmentStatusService {
  private static readonly TOLERANCE_HOURS = 2;

  private static normalizeString(str: string): string {
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }

  private static canViewAllPendingAppointments(userRole?: string): boolean {
    return this.normalizeString(userRole || '') === 'admin';
  }

  private static normalizeDateOnly(input?: string | null): string | null {
    if (!input) return null;
    const parsed = new Date(input);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toISOString().slice(0, 10);
  }

  private static resolveAppointmentInvoiceId(appointment: any): string | null {
    return appointment?.invoiceId || null;
  }

  private static usageIsReversed(usage: any): boolean {
    return usage?.usageStatus === 'reversed' || Boolean(usage?.usageReversedAt);
  }

  static async previewFinancialOrigin(
    appointmentId: string,
    clientId: string,
    serviceType?: string | null,
  ): Promise<FinancialOriginPreview> {
    const { data, error } = await supabase.rpc('financial_a2_preview_appointment_origins', {
      p_client_id: clientId,
      p_appointment_id: appointmentId,
      p_service_type: serviceType ?? null,
    });

    if (error) {
      throw new Error(`Erro ao consultar as origens financeiras disponíveis: ${error.message}`);
    }

    const preview = (data || {}) as Partial<FinancialOriginPreview>;
    return {
      appointmentId: String(preview.appointmentId || appointmentId),
      patientId: preview.patientId ?? null,
      appointmentStartDate: String(preview.appointmentStartDate || ''),
      serviceType: preview.serviceType ?? null,
      recommendedId: preview.recommendedId ?? null,
      options: Array.isArray(preview.options) ? preview.options as FinancialOriginOption[] : [],
      noChargeAvailable: Boolean(preview.noChargeAvailable),
    };
  }

  static async repairPeriodCreditLimits(
    appointmentId: string,
    clientId: string,
  ): Promise<void> {
    const { error } = await supabase.rpc('financial_a6_repair_period_credit_limits', {
      p_client_id: clientId,
      p_appointment_id: appointmentId,
    });

    if (error) {
      throw new Error(`Erro ao sincronizar os limites periódicos do pacote: ${error.message}`);
    }
  }

  static async completeAppointment(
    appointmentId: string,
  userId: string,
  evolutionId?: string,
  financialSelection?: CompleteAppointmentFinancialSelection,
  ): Promise<CompleteAppointmentResult | null> {
    console.log(`Iniciando processo de conclusão para o agendamento: ${appointmentId}`);

    // Passo 1: Obter dados do agendamento e paciente
    const { data: appointment, error: appointmentError } = await supabase
        .from("appointments")
        .select("*, patients(*)")
        .eq("appointmentId", appointmentId)
        .single();

    if (appointmentError) {
        throw new Error(`Erro ao buscar agendamento: ${appointmentError.message}`);
    }
    if (!appointment) {
        throw new Error("Agendamento não encontrado.");
    }
    if (await IntegrationService.isAppointmentLockedForPayment(appointmentId, appointment.clientId)) {
      throw new Error('Este agendamento já foi incluído em um repasse confirmado e não pode ser alterado.');
    }
    const linkedEvolutionId = evolutionId || appointment.appointmentEvolutionId || null;

    // A2: após a instalação da RPC financeira, a conclusão passa por uma
    // operação transacional única que resolve pacote ou contrato/regra de
    // preço e grava a origem no atendimento. O fallback abaixo preserva a
    // compatibilidade enquanto o SQL ainda não foi aplicado em algum ambiente.
    const { data: modernFinancialResult, error: modernFinancialError } = await supabase.rpc(
      'financial_a12_complete_appointment',
      {
        p_client_id: appointment.clientId,
        p_appointment_id: appointmentId,
        p_origin: financialSelection?.origin ?? 'auto',
        // appointmentTitle é legado e pode conter o nome do paciente; não o
        // utilizamos como serviço. A RPC só resolve automaticamente quando
        // existir um único serviço/regra elegível.
        p_service_type: financialSelection?.serviceType ?? null,
        p_contract_pricing_rule_id: financialSelection?.contractPricingRuleId ?? null,
        p_financial_package_id: financialSelection?.financialPackageId ?? null,
        p_user_id: userId,
        p_evolution_id: linkedEvolutionId,
      },
    );
    if (!modernFinancialError) {
      console.log('✅ A2: origem financeira resolvida pela RPC transacional.', modernFinancialResult);
      await AppointmentStatusService.repairPeriodCreditLimits(appointmentId, appointment.clientId);
      return (modernFinancialResult ?? null) as CompleteAppointmentResult | null;
    }
    const isMissingModernRpc = /function .*financial_(?:a2|a12)_complete_appointment|schema cache|does not exist/i.test(modernFinancialError.message);
    if (!isMissingModernRpc) {
      throw new Error(`Erro na integração financeira A2: ${modernFinancialError.message}`);
    }
    console.warn('RPC A2 ainda não aplicada neste ambiente; usando compatibilidade legada.', modernFinancialError.message);

    const resolvedStatus: 'completed' | 'no_evaluated' =
      appointment.appointmentStatus === 'no_billed' || Boolean(linkedEvolutionId)
        ? 'completed'
        : 'no_evaluated';

    const isBillingTriggerStatus =
      appointment.appointmentStatus === 'scheduled' ||
      appointment.appointmentStatus === 'no_billed';
    const shouldTriggerBilling = !appointment.appointmentIsBilled && isBillingTriggerStatus;

    if (appointment.appointmentStatus === resolvedStatus && !shouldTriggerBilling) {
        console.warn(`Agendamento ${appointmentId} já está com status ${resolvedStatus}.`);
        return null;
    }

    if (!shouldTriggerBilling) {
      console.log(`Agendamento ${appointmentId} já faturado ou fora de status faturável. Atualizando apenas status/vínculo de evolução.`);
      const { error: updateOnlyStatusError } = await supabase
        .from("appointments")
        .update({
          appointmentStatus: resolvedStatus,
          appointmentCompletedAt: new Date().toISOString(),
          appointmentResolvedAt: new Date().toISOString(),
          appointmentResolvedBy: userId,
          appointmentPaymentOrigin: resolveAppointmentPaymentOriginPatch({
            currentOrigin: appointment.appointmentPaymentOrigin,
            status: resolvedStatus,
            hasNewFinancialImpact: false,
          }) ?? appointment.appointmentPaymentOrigin ?? 'avulsa',
          ...(linkedEvolutionId ? { appointmentEvolutionId: linkedEvolutionId } : {}),
        })
        .eq("appointmentId", appointmentId);

      if (updateOnlyStatusError) {
        throw new Error(`Erro ao atualizar agendamento sem novo faturamento: ${updateOnlyStatusError.message}`);
      }
      return null;
    }

    const patient = appointment.patients;
    if (!patient) {
        throw new Error("Paciente não vinculado a este agendamento.");
    }

    // Passo 2: Obter o perfil financeiro do paciente para o cliente (tenant) correto
    const { data: financialProfile, error: profileError } = await supabase
        .from("financial_profiles")
        .select("*")
        .eq("patientId", patient.patientId)
        .eq("clientId", appointment.clientId)
        .single();

    if (profileError) {
        throw new Error(`Erro ao buscar perfil financeiro: ${profileError.message}`);
    }
    if (!financialProfile) {
        throw new Error("Perfil financeiro do paciente não encontrado para esta clínica.");
    }

    // Passo 3: Se o paciente tem pacote, buscar o pacote ativo
    let pkg = null;
    if (financialProfile.financialProfileHasPackage) {
        const { data: activePackage, error: pkgError } = await supabase
            .from("packages")
            .select("*")
            .eq("financialProfileId", financialProfile.financialProfileId)
            .eq("clientId", appointment.clientId)
            .gt("packageSessionsRemaining", 0)
            .order("packageCreatedAt", { ascending: false })
            .limit(1)
            .maybeSingle(); // Usar maybeSingle para não dar erro se nenhum pacote for encontrado

        if (pkgError) throw new Error(`Erro ao buscar pacote ativo: ${pkgError.message}`);
        pkg = activePackage; // pkg será null se nenhum pacote ativo for encontrado
    }

    // Caminho 1: Consumir do pacote (se um pacote válido foi encontrado)
    if (pkg) {
        console.log(`Paciente tem pacote com ${pkg.packageSessionsRemaining} sessões restantes. Consumindo uma...`);

        const { error: updatePkgError } = await supabase
            .from("packages")
            .update({ 
                packageSessionsUsed: pkg.packageSessionsUsed + 1,
                packageSessionsRemaining: pkg.packageSessionsRemaining - 1
            })
            .eq("packageId", pkg.packageId)
            .eq("clientId", appointment.clientId);

        if (updatePkgError) {
            throw new Error(`Erro ao atualizar pacote: ${updatePkgError.message}`);
        }

        const { error: usageError } = await supabase.from("package_session_usage").insert({
            clientId: appointment.clientId,
            packageId: pkg.packageId,
            appointmentId: appointmentId,
            usageStatus: 'active',
            usageNotes: `Sessão consumida via Agenda por ${userId}`,
        });

        if (usageError) {
            // Opcional: reverter a atualização do pacote aqui em caso de erro
            throw new Error(`Erro ao registrar uso da sessão: ${usageError.message}`);
        }

        console.log(`Sessão consumida com sucesso. Marcando agendamento como faturado.`);
        const { error: updateAppError } = await supabase.from("appointments").update({
            appointmentStatus: resolvedStatus,
            appointmentIsBilled: true,
            appointmentPaymentOrigin: resolveAppointmentPaymentOriginForBilling('pacote'),
            appointmentCompletedAt: new Date().toISOString(),
            appointmentResolvedAt: new Date().toISOString(),
            appointmentResolvedBy: userId,
            ...(linkedEvolutionId ? { appointmentEvolutionId: linkedEvolutionId } : {}),
        }).eq("appointmentId", appointmentId);

        if (updateAppError) {
            throw new Error(`Erro ao finalizar agendamento: ${updateAppError.message}`);
        }

        console.log(`Agendamento ${appointmentId} concluído e debitado do pacote.`);
        return null;
    }

    // Caminho 2: Faturamento avulso (se nenhum pacote válido foi encontrado)
    console.log("Paciente em modo avulso ou sem sessões no pacote. Gerando cobrança individual.");

    const sessionPrice = financialProfile.financialProfileIndividualSessionPrice;
    if (!sessionPrice || sessionPrice <= 0) {
        throw new Error(
          `MISSING_INDIVIDUAL_SESSION_PRICE: Não foi possível concluir o agendamento de ${patient.patientFullName}. Configure o preço avulso do paciente antes de concluir.`
        );
    }

    const appointmentDate = new Date(appointment.appointmentStartDate);
    const { invoiceId } = await InvoiceService.findOrCreateInvoiceAndAddItem({
        patientId: patient.patientId,
        clientId: financialProfile.clientId,
        description: `Sessão de Fisioterapia - ${appointmentDate.toLocaleDateString("pt-BR")}`,
        amount: sessionPrice,
        type: 'session',
        date: appointmentDate,
    });

    const { error: finalAppError } = await supabase.from("appointments").update({
        appointmentStatus: resolvedStatus,
        appointmentIsBilled: true,
        invoiceId: invoiceId,
        appointmentPaymentOrigin: resolveAppointmentPaymentOriginForBilling('avulsa'),
        appointmentCompletedAt: new Date().toISOString(),
        appointmentResolvedAt: new Date().toISOString(),
        appointmentResolvedBy: userId,
        ...(linkedEvolutionId ? { appointmentEvolutionId: linkedEvolutionId } : {}),
    }).eq("appointmentId", appointmentId);

    if (finalAppError) {
        throw new Error(`Erro ao finalizar agendamento avulso: ${finalAppError.message}`);
    }

    console.log(`Agendamento ${appointmentId} concluído e cobrança avulsa gerada na fatura ${invoiceId}.`);
    return null;
  }

  static async previewReopenNoEvaluated(
    appointmentId: string,
    params: { clientId: string },
  ): Promise<ReopenNoEvaluatedPreviewResult> {
    const { clientId } = params;
    if (!clientId) {
      return {
        canReopen: false,
        requiresSelection: false,
        blockingReasons: ['ClientId é obrigatório para reabrir o agendamento.'],
        appointment: null,
        candidates: [],
      };
    }

    const { data: appointment, error: appointmentError } = await supabase
      .from('appointments')
      .select(`
        appointmentId,
        clientId,
        patientId,
        appointmentStatus,
        appointmentEvolutionId,
        appointmentIsBilled,
        appointmentStartDate,
        appointmentUpdatedAt,
        invoiceId
      `)
      .eq('appointmentId', appointmentId)
      .single();

    if (appointmentError) {
      if (appointmentError.code === 'PGRST116') {
        return {
          canReopen: false,
          requiresSelection: false,
          blockingReasons: ['Agendamento não encontrado para reabertura.'],
          appointment: null,
          candidates: [],
        };
      }
      throw new Error(`Erro técnico ao buscar agendamento para reabertura: ${appointmentError.message}`);
    }
    if (!appointment) {
      return {
        canReopen: false,
        requiresSelection: false,
        blockingReasons: ['Agendamento não encontrado para reabertura.'],
        appointment: null,
        candidates: [],
      };
    }

    const blockingReasons: string[] = [];
    if (appointment.clientId !== clientId) {
      blockingReasons.push('O agendamento não pertence ao tenant ativo.');
    }
    if (appointment.appointmentStatus !== 'no_evaluated') {
      blockingReasons.push('A reabertura só é permitida para agendamentos no status "no_evaluated".');
    }
    if (appointment.appointmentEvolutionId) {
      blockingReasons.push('Não é possível reabrir um agendamento que já possui evolução vinculada.');
    }

    const appointmentDate = this.normalizeDateOnly(appointment.appointmentStartDate);
    const resolvedInvoiceId = this.resolveAppointmentInvoiceId(appointment);

    const { data: usageRows, error: usageError } = await supabase
      .from('package_session_usage')
      .select(`
        usageId,
        clientId,
        packageId,
        appointmentId,
        usageDate,
        usageNotes,
        usageStatus,
        usageReversedAt
      `)
      .eq('appointmentId', appointmentId)
      .eq('clientId', clientId);

    if (usageError) {
      throw new Error(`Erro ao buscar consumo de pacote para reabertura: ${usageError.message}`);
    }

    const packageCandidates: ReopenNoEvaluatedCandidate[] = (usageRows || [])
      .filter((usage: any) => !this.usageIsReversed(usage))
      .map((usage: any) => ({
        id: `usage:${usage.usageId}`,
        kind: 'package_usage',
        title: 'Estornar consumo de pacote',
        subtitle: `Pacote ${String(usage.packageId).slice(0, 8)} • uso ${String(usage.usageId).slice(0, 8)}`,
        meta: {
          usageId: usage.usageId,
          packageId: usage.packageId,
          usageDate: usage.usageDate,
          usageNotes: usage.usageNotes,
          appointmentId: usage.appointmentId,
        },
      }));

    const invoiceCandidates: ReopenNoEvaluatedCandidate[] = [];
    if (resolvedInvoiceId && appointmentDate) {
      const { data: invoiceItems, error: invoiceItemsError } = await supabase
        .from('invoice_items')
        .select(`
          invoiceItemId,
          invoiceId,
          clientId,
          invoiceItemType,
          invoiceItemDescription,
          invoiceItemAmount,
          invoiceItemDate
        `)
        .eq('clientId', clientId)
        .eq('invoiceId', resolvedInvoiceId);

      if (invoiceItemsError) {
        throw new Error(`Erro ao buscar itens de fatura para reabertura: ${invoiceItemsError.message}`);
      }

      (invoiceItems || []).forEach((item: any) => {
        const itemDate = this.normalizeDateOnly(item.invoiceItemDate);
        if (item.invoiceItemType !== 'session') return;
        if (!itemDate || itemDate !== appointmentDate) return;

        invoiceCandidates.push({
          id: `item:${item.invoiceItemId}`,
          kind: 'invoice_item',
          title: 'Estornar item avulso da fatura',
          subtitle: `${item.invoiceItemDescription || 'Sessão avulsa'} • ${itemDate}`,
          amount: Number(item.invoiceItemAmount || 0),
          meta: {
            invoiceItemId: item.invoiceItemId,
            invoiceId: item.invoiceId,
            invoiceItemDate: item.invoiceItemDate,
            invoiceItemType: item.invoiceItemType,
            invoiceItemDescription: item.invoiceItemDescription,
          },
        });
      });
    }

    const candidates = [...packageCandidates, ...invoiceCandidates];
    if (appointment.appointmentIsBilled && candidates.length === 0) {
      blockingReasons.push(
        'Não foi possível identificar um estorno financeiro seguro para este agendamento. Ajuste o financeiro manualmente antes de reabrir.',
      );
    }

    return {
      canReopen: blockingReasons.length === 0,
      requiresSelection: candidates.length > 1,
      blockingReasons,
      appointment: {
        appointmentId: appointment.appointmentId,
        clientId: appointment.clientId,
        patientId: appointment.patientId ?? null,
        appointmentStatus: appointment.appointmentStatus,
        appointmentEvolutionId: appointment.appointmentEvolutionId ?? null,
        appointmentIsBilled: Boolean(appointment.appointmentIsBilled),
        appointmentStartDate: appointment.appointmentStartDate,
        appointmentUpdatedAt: appointment.appointmentUpdatedAt ?? null,
        resolvedInvoiceId,
      },
      candidates,
    };
  }

  static async confirmReopenNoEvaluated(
    appointmentId: string,
    params: ConfirmReopenNoEvaluatedParams,
  ): Promise<ConfirmReopenNoEvaluatedResult> {
    const { clientId, actor, selectedCandidateId, expectedAppointmentUpdatedAt } = params;

    if (!actor?.userId) {
      throw new Error('Usuário executor inválido para reabrir o agendamento.');
    }
    if (!clientId || actor.clientId !== clientId) {
      throw new Error('Tenant inválido para reabrir o agendamento.');
    }

    const preview = await this.previewReopenNoEvaluated(appointmentId, { clientId });
    if (!preview.canReopen || !preview.appointment) {
      throw new Error(preview.blockingReasons[0] || 'Não foi possível reabrir o agendamento.');
    }

    if (
      expectedAppointmentUpdatedAt &&
      preview.appointment.appointmentUpdatedAt &&
      expectedAppointmentUpdatedAt !== preview.appointment.appointmentUpdatedAt
    ) {
      throw new Error('O agendamento foi alterado por outro usuário. Recarregue e tente novamente.');
    }

    const candidates = preview.candidates;
    let selectedCandidate: ReopenNoEvaluatedCandidate | null = null;

    if (candidates.length === 1) {
      selectedCandidate = candidates[0];
      if (selectedCandidateId && selectedCandidateId !== selectedCandidate.id) {
        throw new Error('O candidato selecionado não corresponde ao estorno disponível atualmente.');
      }
    } else if (candidates.length > 1) {
      if (!selectedCandidateId) {
        throw new Error('Selecione o item financeiro que será estornado para reabrir o agendamento.');
      }
      selectedCandidate = candidates.find((candidate) => candidate.id === selectedCandidateId) || null;
      if (!selectedCandidate) {
        throw new Error('O item selecionado não é mais elegível para estorno. Recarregue e tente novamente.');
      }
    } else if (preview.appointment.appointmentIsBilled) {
      throw new Error('Não foi possível localizar estorno financeiro para este agendamento.');
    }

    let reversalApplied: ConfirmReopenNoEvaluatedResult['reversalApplied'] = null;

    if (selectedCandidate?.kind === 'package_usage') {
      const usageId = String(selectedCandidate.meta.usageId || '');
      const packageId = String(selectedCandidate.meta.packageId || '');
      if (!usageId || !packageId) {
        throw new Error('Dados inválidos do consumo de pacote selecionado para estorno.');
      }

      const { data: usageRow, error: usageFetchError } = await supabase
        .from('package_session_usage')
        .select(`
          usageId,
          clientId,
          packageId,
          appointmentId,
          usageStatus,
          usageReversedAt
        `)
        .eq('usageId', usageId)
        .eq('clientId', clientId)
        .eq('appointmentId', appointmentId)
        .eq('packageId', packageId)
        .single();

      if (usageFetchError || !usageRow) {
        throw new Error('Não foi possível validar o consumo de pacote selecionado.');
      }
      if (this.usageIsReversed(usageRow)) {
        throw new Error('O consumo de pacote selecionado já foi estornado.');
      }

      const { data: pkg, error: packageFetchError } = await supabase
        .from('packages')
        .select('packageId, clientId, packageSessionsUsed, packageSessionsRemaining')
        .eq('packageId', packageId)
        .eq('clientId', clientId)
        .single();

      if (packageFetchError || !pkg) {
        throw new Error('Não foi possível validar o pacote vinculado ao consumo selecionado.');
      }

      const previousUsed = Number(pkg.packageSessionsUsed || 0);
      const previousRemaining = Number(pkg.packageSessionsRemaining || 0);
      const nextUsed = Math.max(0, previousUsed - 1);
      const nextRemaining = previousRemaining + 1;

      const { error: packageUpdateError } = await supabase
        .from('packages')
        .update({
          packageSessionsUsed: nextUsed,
          packageSessionsRemaining: nextRemaining,
        })
        .eq('packageId', packageId)
        .eq('clientId', clientId);

      if (packageUpdateError) {
        throw new Error(`Erro ao devolver sessão ao pacote: ${packageUpdateError.message}`);
      }

      const nowIso = new Date().toISOString();
      const { error: usageUpdateError } = await supabase
        .from('package_session_usage')
        .update({
          usageStatus: 'reversed',
          usageReversedAt: nowIso,
          usageReversedBy: actor.userId,
          usageReversalReason: 'Reabertura de agendamento no_evaluated -> scheduled',
          usageReversalContext: {
            action: 'reopen_no_evaluated_to_scheduled',
            appointmentId,
            actorUserId: actor.userId,
            actorRole: actor.userRole || null,
          },
        })
        .eq('usageId', usageId)
        .eq('clientId', clientId)
        .eq('appointmentId', appointmentId)
        .eq('packageId', packageId);

      if (usageUpdateError) {
        // Tentativa best-effort de rollback dos contadores do pacote.
        await supabase
          .from('packages')
          .update({
            packageSessionsUsed: previousUsed,
            packageSessionsRemaining: previousRemaining,
          })
          .eq('packageId', packageId)
          .eq('clientId', clientId);

        throw new Error(`Erro ao registrar reversão do consumo de pacote: ${usageUpdateError.message}`);
      }

      reversalApplied = {
        kind: 'package_usage',
        id: usageId,
      };
    } else if (selectedCandidate?.kind === 'invoice_item') {
      const invoiceItemId = String(selectedCandidate.meta.invoiceItemId || '');
      const invoiceId = String(selectedCandidate.meta.invoiceId || preview.appointment.resolvedInvoiceId || '');
      if (!invoiceItemId || !invoiceId || !preview.appointment.patientId) {
        throw new Error('Dados inválidos do item avulso selecionado para estorno.');
      }

      await InvoiceService.deleteInvoiceItemForAppointmentReopen({
        clientId,
        patientId: preview.appointment.patientId,
        invoiceId,
        invoiceItemId,
        appointmentId,
        actor: {
          userId: actor.userId,
          userRole: actor.userRole || 'doctor',
          displayName: actor.displayName,
          email: actor.email,
          clientId,
        },
        reason: 'Estorno automático ao reabrir agendamento no_evaluated -> scheduled',
      });

      reversalApplied = {
        kind: 'invoice_item',
        id: invoiceItemId,
      };
    }

    const { error: appointmentUpdateError } = await supabase
      .from('appointments')
      .update({
        appointmentStatus: 'scheduled',
        appointmentIsBilled: false,
        invoiceId: null,
        appointmentBillingDate: null,
        appointmentCompletedAt: null,
        appointmentResolvedAt: null,
        appointmentResolvedBy: null,
        appointmentResolutionNotes: null,
        appointmentUpdatedAt: new Date().toISOString(),
      })
      .eq('appointmentId', appointmentId)
      .eq('clientId', clientId);

    if (appointmentUpdateError) {
      throw new Error(`Erro ao reabrir agendamento: ${appointmentUpdateError.message}`);
    }

    return {
      appointmentId,
      previousStatus: preview.appointment.appointmentStatus,
      newStatus: 'scheduled',
      reversalApplied,
    };
  }

  static async getPendingAppointments({
    clientId,
    professionalName,
    userId,
    userRole,
  }: PendingAppointmentsFilters = {}): Promise<PendingAppointment[]> {
    try {
      if (!clientId) {
        console.warn("AppointmentStatusService.getPendingAppointments chamado sem clientId.");
        return [];
      }

      const canViewAllPendingAppointments = this.canViewAllPendingAppointments(userRole);
      const normalizedProfessionalName = professionalName
        ? this.normalizeString(professionalName)
        : '';

      if (!canViewAllPendingAppointments && !userId && !normalizedProfessionalName) {
        console.warn(
          "AppointmentStatusService.getPendingAppointments chamado para usuário não-admin sem userId ou professionalName. Retornando lista vazia por segurança."
        );
        return [];
      }

      const now = new Date();
      const toleranceDate = new Date(now.getTime() - this.TOLERANCE_HOURS * 60 * 60 * 1000);
      let queryBuilder = supabase
        .from("appointments")
        .select(`
          *,
          patients ( * ),
          users!appointments_userId_fkey ( userAssignedCalendarName, userId )
        `)
        .eq("appointmentStatus", "scheduled")
        .eq("clientId", clientId)
        .lt("appointmentEndDate", toleranceDate.toISOString())
        .order("appointmentStartDate", { ascending: false });

      if (!canViewAllPendingAppointments && userId) {
        queryBuilder = queryBuilder.eq("userId", userId);
      }

      const { data: appointmentsData, error } = await queryBuilder;

      if (error) {
        console.error("Erro ao buscar agendamentos do Supabase:", error);
        throw error;
      }

      const pending: PendingAppointment[] = [];

      for (const appointmentRow of appointmentsData) {
        const endDate = new Date(appointmentRow.appointmentEndDate);

        if (!canViewAllPendingAppointments && !userId && normalizedProfessionalName) {
          const normalizedCalendarName = this.normalizeString(appointmentRow.users?.userAssignedCalendarName || '');

          const nameMatches = normalizedCalendarName.includes(normalizedProfessionalName) ||
                             normalizedProfessionalName.includes(normalizedCalendarName);

          if (!nameMatches) {
            console.log(`  ⏭️  Pulando: "${appointmentRow.users?.userAssignedCalendarName || 'N/A'}" não corresponde a "${professionalName}"`);
            continue;
          }
        } else if (canViewAllPendingAppointments) {
          console.log(`  ✅ Admin: incluindo agendamento de "${appointmentRow.users?.userAssignedCalendarName || 'N/A'}"`);
        }

        const hoursSincePassed = Math.floor((now.getTime() - endDate.getTime()) / (1000 * 60 * 60));

        let urgencyLevel: "high" | "medium" | "low" = "low";
        if (hoursSincePassed > 48) {
          urgencyLevel = "high";
        } else if (hoursSincePassed > 24) {
          urgencyLevel = "medium";
        }

        const patientName = appointmentRow.patients?.patientFullName || "Paciente não identificado";

        console.log(`  ✅ Pendente encontrado: ${patientName} - ${hoursSincePassed}h atrás`);

        pending.push({
          id: appointmentRow.appointmentId,
          title: appointmentRow.appointmentTitle || "Sem título",
          start: new Date(appointmentRow.appointmentStartDate),
          end: endDate,
          calendar_name: appointmentRow.users?.userAssignedCalendarName || "Profissional não identificado",
          location: appointmentRow.appointmentLocation,
          patientId: appointmentRow.patientId,
          patientName,
          hoursSincePassed,
          urgencyLevel,
          appointmentStatus: appointmentRow.appointmentStatus,
          appointmentPaymentOrigin: appointmentRow.appointmentPaymentOrigin || null,
          professionalId: appointmentRow.userId,
          clientId: appointmentRow.clientId,
        });
      }

      //console.log("🔔 [AppointmentStatusService] Total de pendentes:", pending.length);
      return pending.sort((a, b) => b.hoursSincePassed - a.hoursSincePassed);
    } catch (error) {
      console.error("Erro ao buscar agendamentos pendentes:", error);
      return [];
    }
  }

  static async getPendingCount(filters: PendingAppointmentsFilters = {}): Promise<number> {
    const pending = await this.getPendingAppointments(filters);
    return pending.length;
  }

  static async resolveAsCompleted(
    appointmentId: string,
    evolutionId: string,
    userId: string,
    notes?: string
  ): Promise<void> {
    try {
      const { data: appointment, error: appointmentFetchError } = await supabase
        .from("appointments")
        .select("appointmentPaymentOrigin")
        .eq("appointmentId", appointmentId)
        .single();

      if (appointmentFetchError) {
        throw appointmentFetchError;
      }
      if (await IntegrationService.isAppointmentLockedForPayment(appointmentId)) {
        throw new Error('Este agendamento já foi incluído em um repasse confirmado e não pode ser alterado.');
      }

      const { error } = await supabase
        .from("appointments")
        .update({
          appointmentResolvedAt: new Date().toISOString(),
          appointmentResolvedBy: userId,
          appointmentResolutionNotes: notes || '',
          appointmentUpdatedAt: new Date().toISOString(),
          appointmentEvolutionId: evolutionId, // Adiciona o ID da evolução
          appointmentStatus: 'completed', // Atualiza o status diretamente aqui
          appointmentCompletedAt: new Date().toISOString(), // Adiciona data de conclusão
          appointmentPaymentOrigin: appointment?.appointmentPaymentOrigin || 'avulsa',
        })
        .eq("appointmentId", appointmentId);

      if (error) {
        throw error;
      }
      console.log(`✅ Agendamento ${appointmentId} marcado como concluído`);
    } catch (error) {
      console.error('Erro ao resolver agendamento como concluído:', error);
      throw error;
    }
  }

  static async previewAbsenceDecision(
    clientId: string,
    appointmentId: string
  ): Promise<AbsenceDecisionPreview> {
    const { data, error } = await supabase.rpc('financial_a17_2_preview_absence', {
      p_client_id: clientId,
      p_appointment_id: appointmentId,
    });

    if (error) {
      console.error('Erro ao carregar a decisão financeira da falta:', error);
      throw error;
    }

    const preview = data as unknown as AbsenceDecisionPreview;
    return {
      ...preview,
      defaultAbsenceNumber: Number(preview.defaultAbsenceNumber || 1),
      options: (preview.options || []).map((option) => ({
        ...option,
        absenceNumber: Number(option.absenceNumber || 1),
        chargeFromAbsence: Number(option.chargeFromAbsence || 1),
        amount: Number(option.amount || 0),
        available: Boolean(option.available),
        isChargeable: Boolean(option.isChargeable),
      })),
    };
  }

  static async resolveAsNoShow(
    input: RegisterAbsenceDecisionInput
  ): Promise<RegisterAbsenceDecisionResult> {
    try {
      const { data, error } = await supabase.rpc('financial_a17_2_register_absence', {
        p_client_id: input.clientId,
        p_appointment_id: input.appointmentId,
        p_absence_type: input.absenceType,
        p_option_id: input.optionId || null,
        p_notes: input.notes || null,
        p_idempotency_key: input.idempotencyKey,
      });

      if (error) throw error;

      // A17.2 registra a decisão; A17.3 aplica o efeito financeiro em uma
      // RPC idempotente. A segunda chamada também permite reprocessar com
      // segurança uma decisão que ficou pendente por indisponibilidade
      // temporária do módulo financeiro.
      const registered = data as unknown as RegisterAbsenceDecisionResult;
      if (!registered.idempotent && registered.status === 'pending') {
        const { data: applied, error: applyError } = await supabase.rpc('financial_a17_3_apply_absence', {
          p_client_id: input.clientId,
          p_absence_event_id: registered.absenceEventId,
        });
        if (applyError) throw applyError;
        return { ...registered, ...(applied as Partial<RegisterAbsenceDecisionResult>) };
      }

      console.log(`✅ Falta registrada para agendamento ${input.appointmentId}`);
      return registered;
    } catch (error) {
      console.error('Erro ao resolver agendamento como falta:', error);
      throw error;
    }
  }

  static async previewNoShowCharge(
    patientId: string,
    clientId: string,
    absenceDate: string,
    appointmentIdToIgnore?: string
  ): Promise<NoShowChargePreview> {
    try {
      const absenceMoment = new Date(absenceDate);
      if (Number.isNaN(absenceMoment.getTime())) {
        return {
          absenceNumber: 1,
          willBeCharged: false,
          chargeAmount: 0,
          monthReference: '',
          openInvoices: [],
          requiresInvoiceSelection: false,
        };
      }

      const monthStart = new Date(absenceMoment.getFullYear(), absenceMoment.getMonth(), 1, 0, 0, 0, 0);
      const monthEnd = new Date(absenceMoment.getFullYear(), absenceMoment.getMonth() + 1, 0, 23, 59, 59, 999);
      const monthReference = `${absenceMoment.getFullYear()}-${String(absenceMoment.getMonth() + 1).padStart(2, '0')}`;

      let noShowQuery = supabase
        .from("appointments")
        .select("appointmentId", { count: "exact", head: true })
        .eq("clientId", clientId)
        .eq("patientId", patientId)
        .eq("appointmentStatus", "no_show")
        .gte("appointmentStartDate", monthStart.toISOString())
        .lte("appointmentStartDate", monthEnd.toISOString());

      if (appointmentIdToIgnore) {
        noShowQuery = noShowQuery.neq("appointmentId", appointmentIdToIgnore);
      }

      const { count: noShowCount, error: noShowError } = await noShowQuery;
      if (noShowError) {
        throw noShowError;
      }

      const absenceNumber = (noShowCount || 0) + 1;

      const { data: financialProfile, error: profileError } = await supabase
        .from("financial_profiles")
        .select("financialProfileIndividualSessionPrice")
        .eq("clientId", clientId)
        .eq("patientId", patientId)
        .maybeSingle();

      if (profileError) {
        throw profileError;
      }

      const basePrice = Number(financialProfile?.financialProfileIndividualSessionPrice || 150);
      const willBeCharged = absenceNumber >= 3;
      const chargeAmount = willBeCharged ? basePrice : 0;
      const { data: openInvoicesData, error: openInvoicesError } = await supabase
        .from('invoices')
        .select('invoiceId, invoiceMonthReference, invoiceTotalAmount, invoiceStatus, invoiceCreatedAt, invoiceDueDate, invoiceNotes')
        .eq('clientId', clientId)
        .eq('patientId', patientId)
        .eq('invoiceMonthReference', monthReference)
        .eq('invoiceStatus', 'open')
        .order('invoiceCreatedAt', { ascending: true });

      if (openInvoicesError) {
        throw openInvoicesError;
      }

      const openInvoices = (openInvoicesData ?? []).map((invoice: any) => ({
        invoiceId: invoice.invoiceId,
        invoiceMonthReference: invoice.invoiceMonthReference,
        invoiceTotalAmount: Number(invoice.invoiceTotalAmount || 0),
        invoiceStatus: invoice.invoiceStatus,
        invoiceCreatedAt: invoice.invoiceCreatedAt || null,
        invoiceDueDate: invoice.invoiceDueDate || null,
        invoiceNotes: invoice.invoiceNotes || null,
      }));

      return {
        absenceNumber,
        willBeCharged,
        chargeAmount,
        monthReference,
        openInvoices,
        requiresInvoiceSelection: willBeCharged && openInvoices.length > 1,
      };
    } catch (error) {
      console.error("Erro ao prever cobrança de falta:", error);
      return {
        absenceNumber: 1,
        willBeCharged: false,
        chargeAmount: 0,
        monthReference: '',
        openInvoices: [],
        requiresInvoiceSelection: false,
      };
    }
  }

  static async resolveAsCancelled(
    appointmentId: string,
    userId: string,
    notes?: string
  ): Promise<void> {
    try {
      if (await IntegrationService.isAppointmentLockedForPayment(appointmentId)) {
        throw new Error('Este agendamento já foi incluído em um repasse confirmado e não pode ser alterado.');
      }
      const { error } = await supabase
        .from("appointments")
        .update({
          appointmentStatus: 'cancelled',
          appointmentResolvedAt: new Date().toISOString(),
          appointmentResolvedBy: userId,
          appointmentResolutionNotes: notes || '',
          appointmentUpdatedAt: new Date().toISOString()
        })
        .eq("appointmentId", appointmentId);

      if (error) {
        throw error;
      }
      console.log(`✅ Agendamento ${appointmentId} marcado como cancelado`);
    } catch (error) {
      console.error('Erro ao resolver agendamento como cancelado:', error);
      throw error;
    }
  }

  static async validateCanMarkAsNoShow(appointmentId: string): Promise<{ valid: boolean; message?: string }> {
    try {
      if (await IntegrationService.isAppointmentLockedForPayment(appointmentId)) {
        return {
          valid: false,
          message: 'Este agendamento já foi incluído em um repasse confirmado e não pode ser alterado.'
        };
      }

      const { data: appointmentData, error } = await supabase
        .from("appointments")
        .select(`
          appointmentEvolutionId,
          appointmentStatus
        `)
        .eq("appointmentId", appointmentId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') { // Não encontrado
          return { valid: false, message: 'Agendamento não encontrado' };
        }
        throw error;
      }

      if (appointmentData.appointmentEvolutionId) {
        return {
          valid: false,
          message: 'Este agendamento já possui evolução registrada. Não é possível marcar como falta.'
        };
      }

      if (appointmentData.appointmentStatus === 'completed') {
        return {
          valid: false,
          message: 'Este agendamento já foi marcado como concluído.'
        };
      }

      return { valid: true };
    } catch (error) {
      console.error('Erro ao validar marcação de falta:', error);
      return { valid: false, message: 'Erro ao validar agendamento' };
    }
  }

  static async getResolutionAuditTrail(appointmentId: string): Promise<AppointmentResolutionAuditEntry[]> {
    const { data, error } = await supabase
      .from('appointment_resolution_log')
      .select(`
        appointmentLogId,
        appointmentId,
        clientId,
        patientId,
        actorUserId,
        actorDisplayName,
        actorEmail,
        previousStatus,
        newStatus,
        resolutionAction,
        notes,
        absenceInfo,
        details,
        createdAt
      `)
      .eq('appointmentId', appointmentId)
      .order('createdAt', { ascending: false });

    if (error) {
      throw new Error(`Erro ao carregar auditoria do agendamento: ${error.message}`);
    }

    return ((data || []) as SupabaseAppointmentResolutionLogRow[]).map((row) => ({
      id: row.appointmentLogId,
      appointmentId: row.appointmentId,
      clientId: row.clientId,
      patientId: row.patientId ?? null,
      actorUserId: row.actorUserId ?? null,
      actorDisplayName: row.actorDisplayName ?? null,
      actorEmail: row.actorEmail ?? null,
      previousStatus: row.previousStatus ?? null,
      newStatus: row.newStatus ?? null,
      action: row.resolutionAction,
      notes: row.notes ?? null,
      absenceInfo: row.absenceInfo,
      details: row.details,
      createdAt: row.createdAt,
    }));
  }
}
